import { useState, useEffect, useCallback } from "react";
import { C, S } from "./theme";
import {
  CUSTOMER_TYPES, CUSTOMER_STATUSES, PAYMENT_TERMS, CURRENCIES,
  VAT_TREATMENTS, ADDRESS_TYPES,
} from "../lib/customers";
import { getPriceLists } from "../lib/priceLists";
import { getCustomerGroups, setCustomerPriceList, assignCustomerToGroup } from "../lib/customerGroups";
import { getCustomerPrices, addCustomerPrice, updateCustomerPrice, removeCustomerPrice } from "../lib/customerPrices";
import { getProducts } from "../lib/products";
import { listUnits } from "../lib/productUnits";
import ProductPicker from "../products/ProductPicker";

// Stable id for new (unsaved) child rows so React keys don't thrash on edit.
let _k = 0;
const tmpId = () => `tmp-${++_k}`;

const blankContact = () => ({ _k: tmpId(), first_name: "", last_name: "", job_title: "", email: "", mobile: "", telephone: "", is_primary: false });
const blankAddress = (type = "delivery") => ({ _k: tmpId(), address_type: type, label: "", line1: "", line2: "", city: "", county: "", postal_code: "", country: "Ireland", is_default: false });

function In({ label, value, onChange, type = "text", placeholder, options }) {
  return (
    <div>
      <label style={S.label}>{label}</label>
      {options ? (
        <select style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input style={S.input} type={type} value={value ?? ""} placeholder={placeholder}
          onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : +e.target.value) : e.target.value)} />
      )}
    </div>
  );
}

// ── Negotiated prices tab (customer_prices via customerPrices.js only) ──────────
function PricesTab({ customerId }) {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [unitsByProduct, setUnitsByProduct] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({ unit_id: "", min_qty: "1", unit_price: "" });
  const [add, setAdd] = useState(null);

  const money = (n) => `€${(Number(n) || 0).toFixed(2)}`;
  const productsById = Object.fromEntries(products.map((p) => [p.id, p]));

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const list = await getCustomerPrices(customerId);
      setRows(list);
      const ids = [...new Set(list.map((r) => r.product_id))];
      const fetched = await Promise.all(ids.map((id) => listUnits(id).catch(() => [])));
      setUnitsByProduct((m) => ({ ...m, ...Object.fromEntries(ids.map((id, i) => [id, fetched[i]])) }));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [customerId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { getProducts({ pageSize: 1000, sortBy: "name" }).then((r) => setProducts(r.data || [])).catch(() => {}); }, []);

  const act = async (fn) => { setBusy(true); setError(""); try { await fn(); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); } };
  const unitOptions = (pid) => (unitsByProduct[pid] || []).filter((u) => !u.is_base);   // base = the null option
  const unitLabel = (r) => { if (!r.unit_id) return "Base"; const u = (unitsByProduct[r.product_id] || []).find((x) => x.id === r.unit_id); return u ? u.code : "unit"; };

  const startAdd = async (product) => {
    if (!unitsByProduct[product.id]) { const us = await listUnits(product.id).catch(() => []); setUnitsByProduct((m) => ({ ...m, [product.id]: us })); }
    setAdd({ product, unit_id: "", min_qty: "1", unit_price: "" });
  };
  const submitAdd = () => {
    if (add.unit_price === "" || Number(add.unit_price) < 0) { setError("Enter a unit price of 0 or more."); return; }
    act(async () => { await addCustomerPrice(customerId, { product_id: add.product.id, unit_id: add.unit_id || null, min_qty: Number(add.min_qty) || 1, unit_price: Number(add.unit_price) }); setAdd(null); });
  };
  const startEdit = (r) => { setEditingId(r.id); setEdit({ unit_id: r.unit_id || "", min_qty: String(r.min_qty), unit_price: String(r.unit_price) }); };
  const saveEdit = (id) => {
    if (edit.unit_price === "" || Number(edit.unit_price) < 0) { setError("Enter a unit price of 0 or more."); return; }
    act(async () => { await updateCustomerPrice(id, { unit_id: edit.unit_id || null, min_qty: Number(edit.min_qty) || 1, unit_price: Number(edit.unit_price) }); setEditingId(null); });
  };

  return (
    <div>
      <div style={{ color: C.textMuted, fontSize: 12.5, marginBottom: 8 }}>Prices negotiated for this customer (the highest-priority tier). Add multiple rows per product at different min quantities for quantity breaks.</div>
      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 10, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "8px 12px", margin: "8px 0", fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={() => setError("")}>Dismiss</button>
        </div>
      )}
      {loading ? <div style={S.empty}>Loading…</div> : (
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Product</th><th style={S.th}>Unit</th><th style={S.th}>Min qty</th><th style={S.th}>Unit price</th><th style={S.th} aria-label="Actions"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td style={S.empty} colSpan={5}>No negotiated prices yet. Add a product below.</td></tr>
            ) : rows.map((r) => {
              const editing = editingId === r.id;
              const prod = productsById[r.product_id];
              return (
                <tr key={r.id}>
                  <td style={S.td}><strong>{prod?.name || r.product_id}</strong>{prod?.sku && <span style={{ color: C.textMuted, fontSize: 11, marginLeft: 6 }}>{prod.sku}</span>}</td>
                  {editing ? (
                    <>
                      <td style={S.td}>
                        <select style={{ ...S.input, width: 120 }} value={edit.unit_id} onChange={(e) => setEdit((p) => ({ ...p, unit_id: e.target.value }))} aria-label="Unit">
                          <option value="">Base</option>
                          {unitOptions(r.product_id).map((u) => <option key={u.id} value={u.id}>{u.code}</option>)}
                        </select>
                      </td>
                      <td style={S.td}><input style={{ ...S.input, width: 70 }} type="number" min="1" step="1" value={edit.min_qty} onChange={(e) => setEdit((p) => ({ ...p, min_qty: e.target.value }))} aria-label="Min qty" /></td>
                      <td style={S.td}><input style={{ ...S.input, width: 90 }} type="number" min="0" step="0.01" value={edit.unit_price} onChange={(e) => setEdit((p) => ({ ...p, unit_price: e.target.value }))} aria-label="Unit price" /></td>
                      <td style={S.td}>
                        <button style={{ ...S.btn(), padding: "3px 8px" }} onClick={() => saveEdit(r.id)} disabled={busy}>Save</button>
                        <button style={{ ...S.btn("ghost"), padding: "3px 8px" }} onClick={() => setEditingId(null)} disabled={busy}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={S.td}>{unitLabel(r)}</td>
                      <td style={S.td}>{r.min_qty}</td>
                      <td style={S.td}><strong>{money(r.unit_price)}</strong></td>
                      <td style={S.td}>
                        <button style={{ ...S.btn("ghost"), padding: "3px 8px" }} aria-label="Edit price" onClick={() => startEdit(r)} disabled={busy}>Edit</button>
                        <button style={{ ...S.btn("ghost"), padding: "3px 8px" }} aria-label="Remove price" onClick={() => { if (window.confirm("Remove this price?")) act(() => removeCustomerPrice(r.id)); }} disabled={busy}>🗑</button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div style={{ ...S.card, marginTop: 10 }}>
        {!add ? (
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ flex: 1, minWidth: 240 }}><ProductPicker products={products} onSelect={startAdd} placeholder="Add a product…" style={{ width: "100%" }} /></div>
            <span style={{ color: C.textMuted, fontSize: 12 }}>{rows.length} price{rows.length === 1 ? "" : "s"}</span>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ minWidth: 160 }}><label style={S.label}>Product</label><div style={{ ...S.input, background: C.surface2 }}>{add.product.name}</div></div>
            <div style={{ width: 130 }}><label style={S.label}>Unit</label>
              <select style={S.input} value={add.unit_id} onChange={(e) => setAdd((p) => ({ ...p, unit_id: e.target.value }))} aria-label="New price unit">
                <option value="">Base</option>
                {unitOptions(add.product.id).map((u) => <option key={u.id} value={u.id}>{u.code}</option>)}
              </select></div>
            <div style={{ width: 90 }}><label style={S.label}>Min qty</label><input style={S.input} type="number" min="1" step="1" value={add.min_qty} onChange={(e) => setAdd((p) => ({ ...p, min_qty: e.target.value }))} aria-label="New price min qty" /></div>
            <div style={{ width: 110 }}><label style={S.label}>Unit price</label><input style={S.input} type="number" min="0" step="0.01" value={add.unit_price} onChange={(e) => setAdd((p) => ({ ...p, unit_price: e.target.value }))} aria-label="New price unit price" placeholder="0.00" /></div>
            <button style={S.btn()} onClick={submitAdd} disabled={busy || add.unit_price === ""}>Add price</button>
            <button style={S.btn("ghost")} onClick={() => setAdd(null)} disabled={busy}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CustomerForm({ initial, onCancel, onSave }) {
  const seed = initial || {};
  const [f, setF] = useState({
    company_name: seed.company_name || "",
    trading_name: seed.trading_name || "",
    customer_type: seed.customer_type || "business",
    status: seed.status || "active",
    vat_number: seed.vat_number || "",
    company_reg_number: seed.company_reg_number || "",
    website: seed.website || "",
    industry: seed.industry || "",
    notes: seed.notes || "",
    payment_terms: seed.payment_terms || "net_30",
    credit_limit: seed.credit_limit ?? "",
    preferred_currency: seed.preferred_currency || "EUR",
    vat_treatment: seed.vat_treatment || "standard",
    customer_reference: seed.customer_reference || "",
  });
  const [contacts, setContacts] = useState(
    (seed.customer_contacts || []).map((p) => ({ ...p, _k: p.id || tmpId() }))
  );
  const [addresses, setAddresses] = useState(
    (seed.customer_addresses || []).map((a) => ({ ...a, _k: a.id || tmpId() }))
  );
  const [tab, setTab] = useState("general");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  // ── Pricing assignment (writes via the WP11b setters — NEVER the customer save
  // payload; these two links save immediately on change, and only for a saved customer).
  const [priceLists, setPriceLists] = useState([]);
  const [groups, setGroups] = useState([]);
  const [plId, setPlId] = useState(seed.price_list_id || "");
  const [groupId, setGroupId] = useState(seed.customer_group_id || "");
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignErr, setAssignErr] = useState("");
  useEffect(() => {
    getPriceLists().then((ls) => setPriceLists(ls.filter((l) => l.status === "active" || l.id === seed.price_list_id))).catch(() => {});
    getCustomerGroups().then(setGroups).catch(() => {});
  }, [seed.price_list_id]);
  const onPriceList = async (v) => {
    setAssignErr(""); setAssignBusy(true); const prev = plId; setPlId(v);
    try { await setCustomerPriceList(seed.id, v || null); } catch (e) { setPlId(prev); setAssignErr(e.message); } finally { setAssignBusy(false); }
  };
  const onGroup = async (v) => {
    setAssignErr(""); setAssignBusy(true); const prev = groupId; setGroupId(v);
    try { await assignCustomerToGroup(seed.id, v || null); } catch (e) { setGroupId(prev); setAssignErr(e.message); } finally { setAssignBusy(false); }
  };

  // ── contacts ──
  const addContact = () => setContacts((p) => [...p, { ...blankContact(), is_primary: p.length === 0 }]);
  const updContact = (k, patch) => setContacts((p) => p.map((c) => (c._k === k ? { ...c, ...patch } : c)));
  const setPrimary = (k) => setContacts((p) => p.map((c) => ({ ...c, is_primary: c._k === k })));
  const delContact = (k) => setContacts((p) => p.filter((c) => c._k !== k));

  // ── addresses ──
  const addAddress = () => setAddresses((p) => [...p, blankAddress()]);
  const updAddress = (k, patch) => setAddresses((p) => p.map((a) => (a._k === k ? { ...a, ...patch } : a)));
  const delAddress = (k) => setAddresses((p) => p.filter((a) => a._k !== k));

  const submit = async () => {
    setError("");
    if (!f.company_name.trim()) { setTab("general"); setError("Company name is required."); return; }
    setBusy(true);
    try {
      await onSave({
        ...f,
        credit_limit: f.credit_limit === "" ? null : f.credit_limit,
        contacts: contacts.map(({ _k, id, ...rest }) => rest),
        addresses: addresses.map(({ _k, id, ...rest }) => rest),
      });
    } catch (e) {
      setError(e.message || "Could not save customer.");
      setBusy(false);
    }
  };

  const TabBtn = ({ id, children }) => (
    <button onClick={() => setTab(id)} type="button"
      style={{ padding: "7px 14px", borderRadius: 7, border: `1px solid ${tab === id ? C.primary : C.border}`,
        background: tab === id ? C.primaryDim : "transparent", color: tab === id ? C.text : C.textMuted,
        fontWeight: 600, fontSize: 12, cursor: "pointer" }}>{children}</button>
  );

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={S.modalBox}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {seed.id ? `Edit ${seed.company_name || "Customer"}` : "New Customer"}
            {seed.customer_number && <span style={{ color: C.textMuted, fontWeight: 500, fontSize: 13, marginLeft: 8 }}>{seed.customer_number}</span>}
          </h2>
          <button onClick={onCancel} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <TabBtn id="general">General</TabBtn>
          <TabBtn id="contacts">Contacts {contacts.length ? `(${contacts.length})` : ""}</TabBtn>
          <TabBtn id="addresses">Addresses {addresses.length ? `(${addresses.length})` : ""}</TabBtn>
          <TabBtn id="commercial">Commercial</TabBtn>
          <TabBtn id="prices">Prices</TabBtn>
        </div>

        {/* PRICES (negotiated customer_prices — writes via customerPrices.js only) */}
        {tab === "prices" && (seed.id
          ? <PricesTab customerId={seed.id} />
          : <div style={{ color: C.textMuted, fontSize: 12.5, padding: "8px 0" }}>Save the customer first, then reopen to add negotiated prices.</div>
        )}

        {/* GENERAL */}
        {tab === "general" && (
          <div>
            <div style={S.grid2}>
              <In label="Company Name *" value={f.company_name} onChange={set("company_name")} placeholder="Acme Ltd" />
              <In label="Trading Name" value={f.trading_name} onChange={set("trading_name")} />
              <In label="Customer Type" value={f.customer_type} onChange={set("customer_type")} options={CUSTOMER_TYPES} />
              <In label="Status" value={f.status} onChange={set("status")} options={CUSTOMER_STATUSES} />
              <In label="VAT Number" value={f.vat_number} onChange={set("vat_number")} placeholder="IE1234567T" />
              <In label="Company Reg. Number" value={f.company_reg_number} onChange={set("company_reg_number")} />
              <In label="Website" value={f.website} onChange={set("website")} placeholder="https://" />
              <In label="Industry" value={f.industry} onChange={set("industry")} />
            </div>
            <div style={{ marginTop: 14 }}>
              <label style={S.label}>Internal Notes</label>
              <textarea style={{ ...S.input, height: 80 }} value={f.notes} onChange={(e) => set("notes")(e.target.value)} />
            </div>
          </div>
        )}

        {/* CONTACTS */}
        {tab === "contacts" && (
          <div>
            {contacts.length === 0 && <div style={{ ...S.empty, padding: "24px 0" }}>No contacts yet.</div>}
            {contacts.map((c) => (
              <div key={c._k} style={{ border: `1px solid ${C.border}`, borderRadius: 9, padding: 14, marginBottom: 12, background: C.surface2 }}>
                <div style={S.grid3}>
                  <In label="First Name" value={c.first_name} onChange={(v) => updContact(c._k, { first_name: v })} />
                  <In label="Last Name" value={c.last_name} onChange={(v) => updContact(c._k, { last_name: v })} />
                  <In label="Job Title" value={c.job_title} onChange={(v) => updContact(c._k, { job_title: v })} />
                  <In label="Email" type="email" value={c.email} onChange={(v) => updContact(c._k, { email: v })} />
                  <In label="Mobile" value={c.mobile} onChange={(v) => updContact(c._k, { mobile: v })} />
                  <In label="Telephone" value={c.telephone} onChange={(v) => updContact(c._k, { telephone: v })} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textMuted, cursor: "pointer" }}>
                    <input type="radio" checked={!!c.is_primary} onChange={() => setPrimary(c._k)} /> Primary contact
                  </label>
                  <button type="button" style={S.btn("ghost")} onClick={() => delContact(c._k)}>Remove</button>
                </div>
              </div>
            ))}
            <button type="button" style={S.btn("secondary")} onClick={addContact}>+ Add Contact</button>
          </div>
        )}

        {/* ADDRESSES */}
        {tab === "addresses" && (
          <div>
            {addresses.length === 0 && <div style={{ ...S.empty, padding: "24px 0" }}>No addresses yet.</div>}
            {addresses.map((a) => (
              <div key={a._k} style={{ border: `1px solid ${C.border}`, borderRadius: 9, padding: 14, marginBottom: 12, background: C.surface2 }}>
                <div style={S.grid3}>
                  <In label="Address Type" value={a.address_type} onChange={(v) => updAddress(a._k, { address_type: v })} options={ADDRESS_TYPES} />
                  <In label="Label" value={a.label} onChange={(v) => updAddress(a._k, { label: v })} placeholder="e.g. Cork Depot" />
                  <In label="Country" value={a.country} onChange={(v) => updAddress(a._k, { country: v })} />
                  <In label="Address Line 1" value={a.line1} onChange={(v) => updAddress(a._k, { line1: v })} />
                  <In label="Address Line 2" value={a.line2} onChange={(v) => updAddress(a._k, { line2: v })} />
                  <In label="Town / City" value={a.city} onChange={(v) => updAddress(a._k, { city: v })} />
                  <In label="County" value={a.county} onChange={(v) => updAddress(a._k, { county: v })} />
                  <In label="Eircode / Postal Code" value={a.postal_code} onChange={(v) => updAddress(a._k, { postal_code: v })} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textMuted, cursor: "pointer" }}>
                    <input type="checkbox" checked={!!a.is_default} onChange={(e) => updAddress(a._k, { is_default: e.target.checked })} /> Default for this type
                  </label>
                  <button type="button" style={S.btn("ghost")} onClick={() => delAddress(a._k)}>Remove</button>
                </div>
              </div>
            ))}
            <button type="button" style={S.btn("secondary")} onClick={addAddress}>+ Add Address</button>
          </div>
        )}

        {/* COMMERCIAL */}
        {tab === "commercial" && (
          <div>
            <div style={S.grid2}>
              <In label="Payment Terms" value={f.payment_terms} onChange={set("payment_terms")} options={PAYMENT_TERMS} />
              <In label="Credit Limit" type="number" value={f.credit_limit} onChange={set("credit_limit")} placeholder="0.00" />
              <In label="Preferred Currency" value={f.preferred_currency} onChange={set("preferred_currency")} options={CURRENCIES} />
              <In label="VAT Treatment" value={f.vat_treatment} onChange={set("vat_treatment")} options={VAT_TREATMENTS} />
              <In label="Customer Reference" value={f.customer_reference} onChange={set("customer_reference")} placeholder="Your reference for this customer" />
            </div>

            <div style={{ marginTop: 18, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
              <div style={S.sectionTitle}>Pricing</div>
              {seed.id ? (
                <>
                  <div style={S.grid2}>
                    <div>
                      <label style={S.label}>Price list</label>
                      <select style={S.input} value={plId} onChange={(e) => onPriceList(e.target.value)} disabled={assignBusy} aria-label="Customer price list">
                        <option value="">None</option>
                        {priceLists.map((l) => <option key={l.id} value={l.id}>{l.name}{l.status !== "active" ? ` (${l.status})` : ""}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={S.label}>Customer group</label>
                      <select style={S.input} value={groupId} onChange={(e) => onGroup(e.target.value)} disabled={assignBusy} aria-label="Customer group">
                        <option value="">None</option>
                        {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ color: C.textDim, fontSize: 11.5, marginTop: 8 }}>
                    {(() => {
                      const groupPl = groups.find((g) => g.id === groupId)?.price_list_id;
                      const applies = plId
                        ? (priceLists.find((l) => l.id === plId)?.name || "the selected price list")
                        : groupPl ? (priceLists.find((l) => l.id === groupPl)?.name || "the group's price list")
                        : "the default / base price";
                      return `Applies: ${applies}. A customer's own price list overrides their group's (resolved when pricing, WP11c). Saved immediately.`;
                    })()}
                    {assignErr && <span style={{ color: C.danger, marginLeft: 8 }}>{assignErr}</span>}
                  </div>
                </>
              ) : (
                <div style={{ color: C.textMuted, fontSize: 12.5 }}>Save the customer first, then reopen to assign a price list or group.</div>
              )}
            </div>
          </div>
        )}

        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 14 }}>{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
          <button type="button" style={S.btn("secondary")} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>
            {busy ? "Saving…" : seed.id ? "Save Changes" : "Create Customer"}
          </button>
        </div>
      </div>
    </div>
  );
}
